import {
  computed,
  createApp,
  defineComponent,
  h,
  onMounted,
  ref,
  watch,
  type Component,
  type DefineComponent,
  type PropType,
} from "vue";
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  type RouteRecordRaw,
  type Router,
} from "vue-router";

export { default as PriceRescueConfirmationDialog } from "./dialogs/PriceRescueConfirmationDialog.vue";
export { default as PriceRescueBadge } from "./primitives/PriceRescueBadge.vue";
export { default as PriceRescueCallout } from "./primitives/PriceRescueCallout.vue";
export { default as PriceRescueButtonLoader } from "./primitives/PriceRescueButtonLoader.vue";
export { default as PriceRescueCheckbox } from "./primitives/PriceRescueCheckbox.vue";
export { default as PriceRescueCursorPagination } from "./primitives/PriceRescueCursorPagination.vue";
export { default as PriceRescueDataTable } from "./primitives/PriceRescueDataTable.vue";
export type {
  PriceRescueComponentTableCell,
  PriceRescueTableCell,
  PriceRescueTableColumn,
  PriceRescueTableRow,
} from "./types/table.js";
export { default as PriceRescueDrawer } from "./primitives/PriceRescueDrawer.vue";
export { default as PriceRescueEmptyListState } from "./primitives/PriceRescueEmptyListState.vue";
export { default as PriceRescueSectionLoader } from "./primitives/PriceRescueSectionLoader.vue";
export { default as PriceRescueSectionBoundary } from "./primitives/PriceRescueSectionBoundary.vue";
export { default as PriceRescueRowActionMenu } from "./primitives/PriceRescueRowActionMenu.vue";
export { default as PriceRescueSelect } from "./primitives/PriceRescueSelect.vue";
export { default as PriceRescueStaticPaginationBar } from "./primitives/PriceRescueStaticPaginationBar.vue";
export { default as PriceRescueStatusRow } from "./primitives/PriceRescueStatusRow.vue";
export { default as PriceRescueStatGrid } from "./primitives/PriceRescueStatGrid.vue";
export { default as PriceRescueInfoCard } from "./primitives/PriceRescueInfoCard.vue";
export { default as PriceRescueTextInput } from "./primitives/PriceRescueTextInput.vue";
export { default as PriceRescueToastRegion } from "./primitives/PriceRescueToastRegion.vue";
export { default as PriceRescueTooltipIcon } from "./primitives/PriceRescueTooltipIcon.vue";
export type { PriceRescueToastItem, PriceRescueToastTone, PriceRescueTone } from "./types/tones.js";

export interface FeatureViewRoute {
  id: string;
  title: string;
}

export interface BuildFeatureViewRoutesOptions<Route extends FeatureViewRoute> {
  defaultPath: string;
  missingViewLabel: string;
  viewComponents: Record<string, () => Promise<DefineComponent>>;
  viewRoutes: readonly Route[];
  toMeta: (route: Route) => Record<string, unknown>;
}

export interface MountVueAppOptions {
  app: Component;
  rootSelector: string;
  router: Router;
}

export interface RenderedHtmlScreenAttachOptions {
  root: HTMLElement;
}

export interface ShellInteractionOptions {
  onRouteChange?: (routeId: string) => void;
  onModalClose?: () => void;
}

export interface ShellInteractionSelectors {
  routeSelector: string;
  routeDatasetKey: string;
  appSelector?: string;
  collapseSelector?: string;
  collapsedClass?: string;
  filterGridSelector?: string;
  filterPanelSelector?: string;
  filterToggleSelector?: string;
  overlaySelector?: string;
  tabSelector?: string;
}

export const RenderedHtmlScreen = defineComponent({
  name: "RenderedHtmlScreen",
  props: {
    attachInteractions: {
      type: Function as PropType<(options: RenderedHtmlScreenAttachOptions) => void>,
      required: true,
    },
    renderView: {
      type: Function as PropType<(viewId: string) => string>,
      required: true,
    },
    viewId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const screenRoot = ref<HTMLElement | null>(null);
    const screenHtml = computed(() => props.renderView(props.viewId));

    onMounted(() => {
      attachRenderedHtmlInteractions(screenRoot.value, props.attachInteractions);
    });

    watch(screenHtml, () => {
      requestAnimationFrame(() =>
        attachRenderedHtmlInteractions(screenRoot.value, props.attachInteractions),
      );
    });

    return () => h("div", { ref: screenRoot, innerHTML: screenHtml.value });
  },
});

export function buildFeatureViewRoutes<Route extends FeatureViewRoute>(
  options: BuildFeatureViewRoutesOptions<Route>,
): RouteRecordRaw[] {
  const defaultRoute = toDefaultRoute(options.defaultPath);
  const featureRoutes = options.viewRoutes.map((route) => toFeatureRouteRecord(route, options));
  return [defaultRoute, ...featureRoutes];
}

export function createFeatureViewRouter(routes: RouteRecordRaw[]): Router {
  return createRouter({
    history: typeof window === "undefined" ? createMemoryHistory() : createWebHashHistory(),
    routes,
  });
}

export function mountVueApp(options: MountVueAppOptions): void {
  const root = document.querySelector<HTMLElement>(options.rootSelector);
  if (root === null) return;

  createApp(options.app).use(options.router).mount(root);
}

export function attachShellInteractions(
  root: ParentNode,
  selectors: ShellInteractionSelectors,
  options: ShellInteractionOptions = {},
): void {
  attachRouteInteractions(root, selectors, options);
  attachTabInteractions(root, selectors);
  attachFilterInteractions(root, selectors);
  attachOverlayInteractions(root, selectors, options);
  attachSidebarInteractions(root, selectors);
}

function attachRenderedHtmlInteractions(
  root: HTMLElement | null,
  attachInteractions: (options: RenderedHtmlScreenAttachOptions) => void,
): void {
  if (root === null) return;

  attachInteractions({ root });
}

function toDefaultRoute(defaultPath: string): RouteRecordRaw {
  return { path: "/", redirect: defaultPath };
}

function toFeatureRouteRecord<Route extends FeatureViewRoute>(
  route: Route,
  options: BuildFeatureViewRoutesOptions<Route>,
): RouteRecordRaw {
  return {
    path: `/${route.id}`,
    name: route.id,
    component: componentForView(route.id, options),
    meta: options.toMeta(route),
  };
}

function componentForView<Route extends FeatureViewRoute>(
  viewId: string,
  options: BuildFeatureViewRoutesOptions<Route>,
): () => Promise<DefineComponent> {
  const component = findViewComponent(viewId, options.viewComponents);
  if (component === undefined) {
    throw new Error(`Missing ${options.missingViewLabel} Vue page for ${viewId}`);
  }

  return component;
}

function findViewComponent(
  viewId: string,
  viewComponents: Record<string, () => Promise<DefineComponent>>,
): (() => Promise<DefineComponent>) | undefined {
  const componentPath = Object.keys(viewComponents).find((path) => path.endsWith(`/${viewId}.vue`));
  return componentPath === undefined ? undefined : viewComponents[componentPath];
}

function attachRouteInteractions(
  root: ParentNode,
  selectors: ShellInteractionSelectors,
  options: ShellInteractionOptions,
): void {
  root.querySelectorAll<HTMLElement>(selectors.routeSelector).forEach((element) => {
    element.addEventListener("click", (event) =>
      handleRouteClick(event, element, selectors, options),
    );
  });
}

function handleRouteClick(
  event: Event,
  element: HTMLElement,
  selectors: ShellInteractionSelectors,
  options: ShellInteractionOptions,
): void {
  const routeId = element.dataset[selectors.routeDatasetKey];
  if (routeId === undefined) return;

  event.preventDefault();
  options.onRouteChange?.(routeId);
}

function attachTabInteractions(root: ParentNode, selectors: ShellInteractionSelectors): void {
  const tabSelector = selectors.tabSelector;
  if (tabSelector === undefined) return;

  root.querySelectorAll<HTMLElement>(tabSelector).forEach((tab) => {
    tab.addEventListener("click", () => activateTab(tab, tabSelector));
  });
}

function activateTab(tab: HTMLElement, tabSelector: string): void {
  tab.parentElement
    ?.querySelectorAll(tabSelector)
    .forEach((item) => item.classList.remove("active"));
  tab.classList.add("active");
}

function attachFilterInteractions(root: ParentNode, selectors: ShellInteractionSelectors): void {
  if (selectors.filterToggleSelector === undefined) return;

  root.querySelectorAll<HTMLElement>(selectors.filterToggleSelector).forEach((button) => {
    button.addEventListener("click", () => toggleFilters(button, selectors));
  });
}

function toggleFilters(button: HTMLElement, selectors: ShellInteractionSelectors): void {
  if (selectors.filterPanelSelector === undefined || selectors.filterGridSelector === undefined)
    return;

  const grid = button
    .closest(selectors.filterPanelSelector)
    ?.querySelector<HTMLElement>(selectors.filterGridSelector);
  if (grid === undefined || grid === null) return;

  grid.hidden = !grid.hidden;
  button.textContent = grid.hidden ? "Show filters" : "Hide filters";
}

function attachOverlayInteractions(
  root: ParentNode,
  selectors: ShellInteractionSelectors,
  options: ShellInteractionOptions,
): void {
  if (selectors.overlaySelector === undefined) return;

  root.querySelectorAll<HTMLElement>(selectors.overlaySelector).forEach((overlay) => {
    overlay.addEventListener("click", (event) => closeOverlayFromBackdrop(event, overlay, options));
  });
}

function closeOverlayFromBackdrop(
  event: Event,
  overlay: HTMLElement,
  options: ShellInteractionOptions,
): void {
  if (event.target !== overlay) return;

  overlay.remove();
  options.onModalClose?.();
}

function attachSidebarInteractions(root: ParentNode, selectors: ShellInteractionSelectors): void {
  const appSelector = selectors.appSelector;
  const collapsedClass = selectors.collapsedClass;
  const collapseSelector = selectors.collapseSelector;
  if (collapseSelector === undefined || appSelector === undefined || collapsedClass === undefined)
    return;

  root.querySelectorAll<HTMLElement>(collapseSelector).forEach((button) => {
    button.addEventListener("click", () =>
      button.closest(appSelector)?.classList.toggle(collapsedClass),
    );
  });
}
